import React, { useCallback, useEffect } from 'react';
import { Stack, RadioButton, Overlay } from '@moda/om';
import { PaymentSource } from '../../../constants';
import { reportError } from '../../../lib/reportError';
import { MIN_AFFIRM_PRICE, MAX_AFFIRM_PRICE } from '../../../constants';
import { useErrorModal } from '../../../hooks/useErrorModal';
import {
  useCart,
  useUpdateCartMutation,
  CartUpdateAction,
  CartUpdatePropertyName
} from '../../../hooks/useCart';
import { useAddressesQuery } from '../../../hooks/useAddress';
import { LoadingBalls } from '../../LoadingBalls';
import { useCountry } from '../../CountrySelector';
import { useCheckoutStepTracking, CheckoutStep } from '../../../hooks/useCheckout';

import './CheckoutPaymentMethodOptions.scss';

export const CheckoutPaymentMethodOptions: React.FC = () => {
  const { country } = useCountry();
  const { showErrorModal } = useErrorModal();
  const {
    cart: {
      attributes: {
        addressId,
        paymentSummary: { payToday, payLater },
        paymentSource
      }
    }
  } = useCart();
  const [updateCart, { loading }] = useUpdateCartMutation();
  const { addresses } = useAddressesQuery();
  const { trackCheckoutStepComplete } = useCheckoutStepTracking(CheckoutStep.PaymentMethod);

  const handleChange = useCallback(
    async (newPaymentSource: PaymentSource) => {
      if (newPaymentSource === paymentSource) return;

      try {
        await updateCart(CartUpdateAction.PaymentSource, {
          [CartUpdatePropertyName.PaymentSource]: newPaymentSource
        });

        trackCheckoutStepComplete();
      } catch (error) {
        reportError(error);
        showErrorModal();
      }
    },
    [paymentSource, updateCart, trackCheckoutStepComplete, showErrorModal]
  );

  const selectedAddress = addresses.find(address => Number(address.id) === addressId);

  const isAffirmEnabled =
    country.alpha2Code === 'US' &&
    selectedAddress?.attributes.countryCode === 'US' &&
    Number(payLater.price) === 0 &&
    payToday.price >= MIN_AFFIRM_PRICE &&
    payToday.price <= MAX_AFFIRM_PRICE;

  // if Affirm is selected and it becomes unavailable (e.g. paying everything with site credit) change to Adyen
  useEffect(() => {
    if (!isAffirmEnabled && paymentSource === PaymentSource.Affirm) {
      updateCart(CartUpdateAction.PaymentSource, {
        [CartUpdatePropertyName.PaymentSource]: PaymentSource.Adyen
      }).then(trackCheckoutStepComplete);
    }
  }, [isAffirmEnabled, paymentSource, trackCheckoutStepComplete, updateCart]);

  if (!isAffirmEnabled) return null;

  return (
    <div className="CheckoutPaymentMethodOptions">
      <Stack direction="horizontal" space={5}>
        <RadioButton
          checked={paymentSource === PaymentSource.Adyen}
          onChange={() => handleChange(PaymentSource.Adyen)}
        >
          Debit/Credit Card
        </RadioButton>

        <RadioButton
          checked={paymentSource === PaymentSource.Affirm}
          onChange={() => handleChange(PaymentSource.Affirm)}
        >
          <img className="CheckoutPaymentMethodOptions__logo" alt="Affirm" src="/affirm.png" />
        </RadioButton>
      </Stack>

      <Overlay show={loading}>
        <LoadingBalls />
      </Overlay>
    </div>
  );
};
